import type { Signal } from "@angular/core";
import { S25TaskEditComponent } from "../../modules/s25-task/s25.task.edit.component";
import { S25TaskActionComponent } from "../../modules/s25-task/s25.task.action.component";
import { S25TaskStatusComponent } from "../../modules/s25-task/s25.task.status.component";
import { TableEditableDateComponent } from "../../modules/s25-table/generics/table-editable-date.component";
import { S25TableComponent } from "../../modules/s25-table/s25.table.component";
import type { Table } from "../../modules/s25-table/Table";
import { TaskService } from "../../services/task/task.service";
import type { Task } from "../../pojo/Task";
import { ListTableHelper } from "./list.table.helper";
import type { ListCellValue, ListColumn, ListDateFormats, ListTableColumnDefaultsOptions } from "./list.table.types";

export type SearchListCellContext = {
    getWorkflowChained: () => boolean;
    getUserId: () => number;
    onTaskAction: (state: Task.State, context: Table.CellCallbackContext) => void;
};

type TaskCellValue = ListCellValue & {
    taskBlocked?: boolean;
    task_type?: string | number;
    task_id?: number;
    event_id?: number;
    assigned_to_id?: number;
    respond_by?: Date;
};

export class SearchListTableHelper extends ListTableHelper {
    constructor(
        table: Signal<S25TableComponent | undefined>,
        private readonly searchContext: SearchListCellContext,
    ) {
        super(table);
    }

    override applyTableColumnDefaults(
        tableColumn: Table.Column,
        column: ListColumn,
        options: ListTableColumnDefaultsOptions = {},
    ): void {
        super.applyTableColumnDefaults(tableColumn, column, options);

        switch (column.prefname) {
            case "task_item":
                tableColumn.cells = { component: S25TaskEditComponent };
                break;
            case "status":
                tableColumn.cells = { component: S25TaskStatusComponent };
                break;
            case "actions":
                tableColumn.cells = { component: S25TaskActionComponent };
                break;
            case "respond_by":
                tableColumn.cells = { component: TableEditableDateComponent };
                tableColumn.minWidth = 80;
                break;
        }
    }

    override mapCell(column: ListColumn, data: ListCellValue, formatsOverride?: ListDateFormats): [string, Table.Cell] {
        const taskCell = this.mapTaskCell(column, data);
        if (taskCell) {
            return taskCell;
        }

        return super.mapCell(column, data, formatsOverride);
    }

    private mapTaskCell(column: ListColumn, data: ListCellValue): [string, Table.Cell] | undefined {
        const taskData = data as TaskCellValue;

        switch (column.prefname) {
            case "task_item":
                return [
                    column.prefname,
                    {
                        inputs: {
                            showIcon: true,
                            taskBlocked: this.searchContext.getWorkflowChained() && taskData.taskBlocked,
                            taskModel: taskData,
                        },
                        outputs: { onRefresh: () => this.refreshTable() },
                    },
                ];
            case "status":
                return [column.prefname, { inputs: { task: taskData } }];
            case "actions":
                return [
                    column.prefname,
                    { inputs: taskData, outputs: { stateChange: this.searchContext.onTaskAction } },
                ];
            case "respond_by":
                return [
                    column.prefname,
                    {
                        inputs: {
                            val: taskData.respond_by,
                            readOnly: taskData.assigned_to_id !== this.searchContext.getUserId(),
                        },
                        outputs: {
                            valChange: (val: Date) => {
                                const isTodo =
                                    parseInt(String(taskData.task_type)) === 5 ||
                                    parseInt(String(taskData.task_type)) === 6;
                                if (isTodo) {
                                    TaskService.putTodoDueDate(taskData.task_id!, val);
                                } else {
                                    TaskService.putEventTaskDueDate(taskData.event_id!, taskData.task_id!, val);
                                }
                            },
                        },
                    },
                ];
            default:
                return undefined;
        }
    }
}
